import './style.css';
import { showScreen, showToast, setLoadingMessage, escapeHtml, formatTime } from './ui.js';
import { startCamera, stopCamera, flipCamera, captureFrame } from './camera.js';
import { identify, identifyByText } from './identifier.js';
import { narrate } from './narrator.js';
import { Speaker } from './speaker.js';
import { saveVisit, listVisits, getVisit, deleteVisit, generateId, saveTerm, listTerms, deleteTerm } from './cache.js';
import { fetchMuseums, fetchNearby, getCurrentPosition, translateImageApi, askApi, extractTermsApi, compareWorksApi, fetchExhibitionsApi, generatePreparationApi } from './api.js';
import { Voice } from './voice.js';

const speaker = new Speaker();
const titleSpeaker = new Speaker();
const voice = new Voice();
let voiceTargetInputId = null; // どの input に書き込むか
let currentVisit = null;
let lastIdentified = null;
let museumsCache = null;
let selectedMuseum = null;
let wishlist = [];
let lastVisitTitle = null;

// 履歴フィルタ
let historyAll = [];
let historyFilter = { search: '', genre: 'all' };

// カメラの利用先（識別 or 翻訳）
let cameraMode = 'identify';

// プレイヤー速度
const SPEED_OPTIONS = [1.0, 1.25, 1.5, 0.75];
let speedIdx = 0;

// チャット
let chatHistory = [];

// 比較ビュー用の選択中作品
let compareTargets = []; // [{visitId, title, artist, year, medium, summary}]
let glossaryAll = [];
let glossaryFilter = '';

// 展覧会・予習
let currentExhibitions = null;
let currentExhibition = null;
let currentPreparation = null;

// 解説モード
const NARRATE_MODES = [
  { value: 'standard', label: '標準', desc: '700-900字。学芸員視点で6段構成の深い解説' },
  { value: 'beginner', label: '初心者向け', desc: '300-400字。専門用語を使わず要点だけ' },
  { value: 'expert', label: '専門的', desc: '1500-1800字。図像学・様式論・引用を交えた論考' },
  { value: 'story', label: '物語風', desc: '500-700字。作家の人生と時代を文学的に語る' },
  { value: 'kids', label: '子供向け', desc: '200-300字。小学生でも分かる易しい言葉' },
];
let preferredNarrateMode = 'standard';

const SELECTED_MUSEUM_KEY = 'mg.selectedMuseum';
const WISHLIST_KEY = 'mg.wishlist';
const SPEED_KEY = 'mg.speed';
const NARRATE_MODE_KEY = 'mg.narrateMode';

const GENRE_LABELS = {
  painting: '絵画', drawing: '素描', print: '版画',
  sculpture: '彫刻', photography: '写真',
  ceramic: '陶磁器', textile: '染織', decorative: '工芸',
  installation: 'インスタレーション', video: '映像',
  architecture: '建築', artifact: '遺物', other: 'その他',
};
const HISTORY_GENRE_FILTERS = [
  { value: 'all', label: 'すべて' },
  { value: 'painting', label: '絵画' },
  { value: 'sculpture', label: '彫刻' },
  { value: 'photography', label: '写真' },
  { value: 'print', label: '版画' },
  { value: 'ceramic', label: '陶磁器' },
  { value: 'decorative', label: '工芸' },
  { value: 'artifact', label: '遺物' },
  { value: 'other', label: 'その他' },
];
function genreLabel(g) { return GENRE_LABELS[g] || GENRE_LABELS.other; }

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
} else if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((regs) => {
    for (const r of regs) r.unregister();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  loadSelectedMuseum();
  loadWishlist();
  loadSpeed();
  loadNarrateMode();
  bindHomeScreen();
  bindCameraScreen();
  bindResultScreen();
  bindHistoryScreen();
  bindCandidatesScreen();
  bindMuseumsScreen();
  bindNearbyScreen();
  bindSearchScreen();
  bindTranslateScreen();
  bindChatScreen();
  bindModeScreen();
  bindGlossaryScreen();
  bindCompareScreen();
  bindExhibitionsScreen();
  bindPreparationScreen();
  bindVoiceInputs();
  renderRecentOnHome();
  renderSelectedMuseum();
  renderWishlistOnHome();
  updateModeLabel();
});

function bindHomeScreen() {
  document.getElementById('btn-capture').addEventListener('click', async () => {
    cameraMode = 'identify';
    await openCamera();
  });
  document.getElementById('btn-translate').addEventListener('click', async () => {
    cameraMode = 'translate';
    await openCamera();
  });
  document.getElementById('btn-history').addEventListener('click', async () => {
    await renderHistory();
    showScreen('history');
  });
  document.getElementById('btn-recent-more').addEventListener('click', async () => {
    await renderHistory();
    showScreen('history');
  });
  document.getElementById('btn-pick-museum').addEventListener('click', async () => {
    await renderMuseumsList();
    showScreen('museums');
  });
  document.getElementById('btn-find-nearby').addEventListener('click', () => {
    document.getElementById('nearby-list').innerHTML = '';
    setNearbyStatus('');
    showScreen('nearby');
  });
  document.getElementById('btn-search').addEventListener('click', () => {
    showScreen('search');
    setTimeout(() => document.getElementById('search-title').focus(), 100);
  });
  document.getElementById('btn-clear-museum').addEventListener('click', () => {
    setSelectedMuseum(null);
  });
  document.getElementById('btn-glossary').addEventListener('click', async () => {
    await openGlossary();
  });
  document.getElementById('btn-exhibitions').addEventListener('click', async () => {
    await openExhibitions();
  });
}

function bindCameraScreen() {
  document.getElementById('btn-camera-back').addEventListener('click', async () => {
    await stopCamera();
    showScreen('home');
  });
  document.getElementById('btn-camera-flip').addEventListener('click', async () => {
    const videoEl = document.getElementById('camera-video');
    try { await flipCamera(videoEl); } catch (e) { showToast('カメラの切替に失敗しました'); }
  });
  document.getElementById('btn-shutter').addEventListener('click', async () => {
    await onShutter();
  });
}

function bindResultScreen() {
  document.getElementById('btn-result-back').addEventListener('click', async () => {
    speaker.pause();
    titleSpeaker.pause();
    setPlayIcon(false);
    showScreen('home');
    await renderRecentOnHome();
  });
  document.getElementById('btn-result-save').addEventListener('click', async () => {
    if (!currentVisit) return;
    try {
      await saveVisit(currentVisit);
      showToast('履歴に保存しました');
    } catch (e) {
      showToast('保存に失敗しました');
    }
  });
  document.getElementById('btn-play').addEventListener('click', async () => {
    await togglePlay();
  });
  document.getElementById('btn-skip-back').addEventListener('click', () => {
    speaker.skip(-15);
  });
  document.getElementById('btn-skip-forward').addEventListener('click', () => {
    speaker.skip(30);
  });
  document.getElementById('btn-speed').addEventListener('click', () => {
    cyclePlaybackRate();
  });
  document.getElementById('result-original').addEventListener('click', async () => {
    await playOriginalTitle();
  });
  document.getElementById('btn-route-camera').addEventListener('click', async () => {
    speaker.pause(); titleSpeaker.pause(); setPlayIcon(false);
    cameraMode = 'identify';
    await openCamera();
  });
  document.getElementById('btn-route-search').addEventListener('click', () => {
    speaker.pause(); titleSpeaker.pause(); setPlayIcon(false);
    showScreen('search');
    setTimeout(() => document.getElementById('search-title').focus(), 100);
  });
  document.getElementById('btn-result-ask').addEventListener('click', () => {
    speaker.pause(); setPlayIcon(false);
    openChat();
  });
  document.getElementById('btn-result-mode').addEventListener('click', () => {
    speaker.pause(); setPlayIcon(false);
    renderModeList();
    showScreen('mode');
  });
  document.getElementById('btn-result-compare').addEventListener('click', () => {
    speaker.pause(); setPlayIcon(false);
    openCompareFromResult();
  });

  speaker.onTimeUpdate = (current, duration) => {
    const bar = document.getElementById('progress-bar');
    const time = document.getElementById('result-time');
    if (duration && Number.isFinite(duration)) {
      bar.style.width = `${(current / duration) * 100}%`;
    }
    time.textContent = formatTime(current);
  };

  speaker.onEnded = () => { setPlayIcon(false); };
}

function bindHistoryScreen() {
  document.getElementById('btn-history-back').addEventListener('click', async () => {
    showScreen('home');
    await renderRecentOnHome();
  });
  document.getElementById('history-search').addEventListener('input', (event) => {
    historyFilter.search = event.target.value.trim();
    renderHistoryList();
  });
}

function bindCandidatesScreen() {
  document.getElementById('btn-candidates-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('btn-candidates-none').addEventListener('click', async () => {
    if (!lastIdentified) return showScreen('home');
    await runNarrationAndSpeak({
      mode: 'visual',
      visualDescription: lastIdentified.visualDescription || lastIdentified.summary_hint || '',
    }, { genre: lastIdentified.genre || 'other' });
  });
}

function bindMuseumsScreen() {
  document.getElementById('btn-museums-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('museums-search').addEventListener('input', (event) => {
    renderMuseumsList(event.target.value);
  });
}

function bindNearbyScreen() {
  document.getElementById('btn-nearby-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('btn-nearby-locate').addEventListener('click', async () => {
    await runNearbySearch();
  });
}

function bindSearchScreen() {
  document.getElementById('btn-search-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('search-form').addEventListener('submit', async (event) => {
    event.preventDefault();
    await onSearchSubmit();
  });
}

function bindTranslateScreen() {
  document.getElementById('btn-translate-back').addEventListener('click', () => {
    speaker.pause(); setPlayIcon(false);
    showScreen('home');
  });
}

function bindChatScreen() {
  document.getElementById('btn-chat-back').addEventListener('click', () => {
    showScreen('result');
  });
  document.getElementById('chat-form').addEventListener('submit', async (event) => {
    event.preventDefault();
    await onChatSubmit();
  });
}

function bindModeScreen() {
  document.getElementById('btn-mode-back').addEventListener('click', () => {
    showScreen('result');
  });
}

function bindGlossaryScreen() {
  document.getElementById('btn-glossary-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('glossary-search').addEventListener('input', (event) => {
    glossaryFilter = event.target.value.trim();
    renderGlossaryList();
  });
}

function bindCompareScreen() {
  document.getElementById('btn-compare-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('btn-compare-run').addEventListener('click', async () => {
    await runCompare();
  });
}

function bindVoiceInputs() {
  if (!voice.isSupported()) {
    // 非対応ブラウザではマイクボタンを隠す
    document.querySelectorAll('.btn--mic').forEach((b) => { b.hidden = true; });
    return;
  }
  document.getElementById('btn-voice-title').addEventListener('click', () => startVoice('search-title'));
  document.getElementById('btn-voice-artist').addEventListener('click', () => startVoice('search-artist'));
}

function startVoice(targetInputId) {
  if (voice.isListening) {
    voice.stop();
    setVoiceButtonsActive(false);
    return;
  }
  voiceTargetInputId = targetInputId;
  setVoiceButtonsActive(true, targetInputId);

  voice.onResult = (transcript) => {
    const input = document.getElementById(voiceTargetInputId);
    if (input) {
      input.value = transcript;
      input.focus();
    }
    showToast(`「${transcript}」を入力しました`);
  };
  voice.onError = (err) => {
    showToast(err?.message || '音声認識に失敗しました');
    setVoiceButtonsActive(false);
  };
  voice.onEnd = () => {
    setVoiceButtonsActive(false);
  };

  voice.start('ja-JP');
  showToast('話してください…');
}

function setVoiceButtonsActive(active, targetId = null) {
  document.querySelectorAll('.btn--mic').forEach((b) => {
    b.classList.toggle('btn--mic--active', active && b.id === `btn-voice-${targetId === 'search-title' ? 'title' : 'artist'}`);
  });
}

// ===== Selected museum =====
function loadSelectedMuseum() {
  try {
    const raw = localStorage.getItem(SELECTED_MUSEUM_KEY);
    if (raw) selectedMuseum = JSON.parse(raw);
  } catch { selectedMuseum = null; }
}

function setSelectedMuseum(museum) {
  selectedMuseum = museum;
  if (museum) localStorage.setItem(SELECTED_MUSEUM_KEY, JSON.stringify(museum));
  else localStorage.removeItem(SELECTED_MUSEUM_KEY);
  renderSelectedMuseum();
}

function renderSelectedMuseum() {
  const el = document.getElementById('selected-museum');
  if (!el) return;
  if (!selectedMuseum) { el.hidden = true; }
  else {
    el.hidden = false;
    document.getElementById('selected-museum-name').textContent =
      selectedMuseum.nameJa || selectedMuseum.name || '';
  }
  // 展覧会ボタンの表示制御（内蔵60館の id を持つもののみ）
  const exhibitionsBtn = document.getElementById('btn-exhibitions');
  if (exhibitionsBtn) {
    const isInternalMuseum = selectedMuseum && selectedMuseum.id && !selectedMuseum.id.startsWith('osm_');
    exhibitionsBtn.hidden = !isInternalMuseum;
  }
}

// ===== Wishlist =====
function loadWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    wishlist = raw ? JSON.parse(raw) : [];
  } catch { wishlist = []; }
}
function saveWishlist() { localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist)); }
function isInWishlist(id) { return wishlist.some((m) => m.id === id); }

function toggleWishlist(museum) {
  const idx = wishlist.findIndex((m) => m.id === museum.id);
  if (idx >= 0) {
    wishlist.splice(idx, 1);
    showToast('リストから削除しました');
  } else {
    wishlist.push({
      id: museum.id, name: museum.name, nameJa: museum.nameJa,
      city: museum.city, country: museum.country,
      category: museum.category, language: museum.language,
      coordinates: museum.coordinates,
    });
    showToast('行きたいリストに追加しました');
  }
  saveWishlist();
  renderWishlistOnHome();
}

function renderWishlistOnHome() {
  const section = document.getElementById('home-wishlist');
  const list = document.getElementById('wishlist-list');
  if (!section || !list) return;
  if (wishlist.length === 0) { section.hidden = true; return; }
  section.hidden = false;
  list.innerHTML = '';
  for (const m of wishlist) {
    const li = document.createElement('li');
    li.className = 'recent__item';
    const meta = [m.city, m.country].filter(Boolean).join(' / ');
    li.innerHTML = `
      <div class="recent__body">
        <h3 class="recent__title">${escapeHtml(m.nameJa || m.name)}</h3>
        <p class="recent__meta">${escapeHtml(meta)}</p>
      </div>
    `;
    li.addEventListener('click', () => {
      setSelectedMuseum(m);
      showToast(`${m.nameJa || m.name} を選択中の館に設定`);
    });
    list.appendChild(li);
  }
}

// ===== Museums list =====
async function renderMuseumsList(filter = '') {
  const list = document.getElementById('museums-list');
  const empty = document.getElementById('museums-empty');
  if (!museumsCache) {
    list.innerHTML = '';
    empty.hidden = true;
    list.innerHTML = '<li class="hint" style="padding:16px 0">読み込み中...</li>';
    try { museumsCache = await fetchMuseums(); }
    catch (e) {
      list.innerHTML = '';
      showToast(e?.message || '美術館の取得に失敗しました');
      return;
    }
  }

  const q = filter.trim().toLowerCase();
  const filtered = q
    ? museumsCache.filter((m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.nameJa || '').toLowerCase().includes(q) ||
        (m.city || '').toLowerCase().includes(q) ||
        (m.country || '').toLowerCase().includes(q))
    : museumsCache;

  list.innerHTML = '';
  empty.hidden = filtered.length > 0;
  for (const m of filtered) {
    list.appendChild(buildMuseumItem(m, () => {
      setSelectedMuseum(m);
      showToast(`${m.nameJa || m.name} を選択しました`);
      showScreen('home');
    }));
  }
}

function buildMuseumItem(m, onSelect, extraInfo = null) {
  const li = document.createElement('li');
  li.className = 'venue';
  if (selectedMuseum && selectedMuseum.id === m.id) li.classList.add('venue--selected');

  const body = document.createElement('div');
  body.className = 'venue__body';
  const categoryLabel = categoryLabelOf(m.category);
  body.innerHTML = `
    <p class="venue__name">${escapeHtml(m.nameJa || m.name)}</p>
    ${m.nameJa && m.name && m.name !== m.nameJa ? `<p class="venue__name--latin">${escapeHtml(m.name)}</p>` : ''}
    <div class="venue__meta">
      <span class="venue__category">${escapeHtml(categoryLabel)}</span>
      <span>${escapeHtml(m.city || '')} / ${escapeHtml(m.country || '')}</span>
      ${extraInfo?.distance != null ? `<span class="venue__distance">${formatDistance(extraInfo.distance)}</span>` : ''}
    </div>
    ${extraInfo?.address ? `<p class="venue__address">${escapeHtml(extraInfo.address)}</p>` : ''}
  `;
  body.addEventListener('click', () => onSelect(m));

  const star = document.createElement('button');
  star.className = 'venue__star' + (isInWishlist(m.id) ? ' venue__star--active' : '');
  star.type = 'button';
  star.setAttribute('aria-label', '行きたいリスト');
  star.innerHTML = `<svg class="icon"><use href="#i-star"/></svg>`;
  star.addEventListener('click', (event) => {
    event.stopPropagation();
    toggleWishlist(m);
    star.classList.toggle('venue__star--active', isInWishlist(m.id));
  });

  li.appendChild(body);
  li.appendChild(star);
  return li;
}

function categoryLabelOf(category) {
  switch (category) {
    case 'art_museum': return '美術館';
    case 'contemporary_art': return '現代美術';
    case 'history_museum': return '博物館';
    case 'photography_museum': return '写真';
    case 'art_gallery': case 'gallery': return 'ギャラリー';
    case 'arts_centre': return '芸術文化施設';
    case 'ethnography': return '民族学';
    case 'museum': return '美術館・博物館';
    default: return '芸術施設';
  }
}

function formatDistance(km) {
  if (km == null || !Number.isFinite(km)) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
}

// ===== Nearby =====
async function runNearbySearch() {
  const list = document.getElementById('nearby-list');
  list.innerHTML = '';
  setNearbyStatus('現在地を取得しています…');

  let pos;
  try { pos = await getCurrentPosition(); }
  catch (err) { setNearbyStatus(err?.message || '現在地の取得に失敗しました'); return; }

  setNearbyStatus(`周辺の施設を検索しています…（${pos.lat.toFixed(3)}, ${pos.lon.toFixed(3)}）`);

  let items = [];
  try { items = await fetchNearby(pos.lat, pos.lon, 5000); }
  catch (err) { setNearbyStatus(err?.message || '検索に失敗しました'); return; }

  if (items.length === 0) { setNearbyStatus('近隣に施設が見つかりませんでした。'); return; }
  setNearbyStatus(`${items.length} 件見つかりました（半径5km）`);

  for (const item of items) {
    const m = {
      id: item.id, name: item.nameEn || item.name, nameJa: item.nameJa || item.name,
      city: item.address || '', country: '', category: item.category,
      coordinates: item.coordinates, language: 'en',
    };
    list.appendChild(buildMuseumItem(m, () => {
      setSelectedMuseum(m);
      showToast(`${m.nameJa || m.name} を選択しました`);
      showScreen('home');
    }, { distance: item.distance, address: item.address }));
  }
}

function setNearbyStatus(text) {
  const el = document.getElementById('nearby-status');
  if (!el) return;
  if (!text) { el.hidden = true; el.textContent = ''; }
  else { el.hidden = false; el.textContent = text; }
}

// ===== Camera flow =====
async function openCamera() {
  showScreen('camera');
  const videoEl = document.getElementById('camera-video');
  try { await startCamera(videoEl); }
  catch (err) {
    showToast('カメラを利用できません: ' + (err?.message || ''));
    showScreen('home');
  }
}

async function onShutter() {
  const videoEl = document.getElementById('camera-video');
  const canvasEl = document.getElementById('camera-canvas');
  let blob;
  try { blob = await captureFrame(videoEl, canvasEl); }
  catch (e) { showToast('撮影に失敗しました。もう一度お試しください'); return; }
  await stopCamera();

  if (cameraMode === 'translate') {
    await processTranslate(blob);
  } else {
    await processIdentifyFromCamera(blob);
  }
}

async function processIdentifyFromCamera(blob) {
  setLoadingMessage('作品を識別しています');
  showScreen('loading');
  try {
    const hint = {};
    if (selectedMuseum?.id) hint.museumId = selectedMuseum.id;
    if (lastVisitTitle && selectedMuseum) hint.previousTitle = lastVisitTitle;
    const identified = await identify(blob, hint);
    lastIdentified = identified;
    await processIdentified(identified, blob);
  } catch (err) {
    showToast(err?.message || '識別に失敗しました');
    showScreen('home');
  }
}

async function processTranslate(blob) {
  setLoadingMessage('テキストを翻訳しています');
  showScreen('loading');

  let result;
  try { result = await translateImageApi(blob); }
  catch (err) {
    showToast(err?.message || '翻訳に失敗しました');
    showScreen('home');
    return;
  }

  // 表示
  const imgEl = document.getElementById('translate-image');
  imgEl.src = URL.createObjectURL(blob);
  imgEl.hidden = false;

  if (!result.japaneseTranslation && !result.sourceText) {
    document.getElementById('translate-content').hidden = true;
    document.getElementById('translate-empty').hidden = false;
    document.getElementById('translate-empty').textContent = result.summary || '画像から文字情報を読み取れませんでした。';
    showScreen('translate');
    return;
  }

  document.getElementById('translate-content').hidden = false;
  document.getElementById('translate-empty').hidden = true;
  document.getElementById('translate-ja').textContent = result.japaneseTranslation || '';
  document.getElementById('translate-source').textContent = result.sourceText || '';
  document.getElementById('translate-lang').textContent = (result.sourceLanguage || '?').toUpperCase();

  // 日本語を読み上げ
  const ttsText = result.japaneseTranslation || result.summary || '';
  if (ttsText) {
    try { await speaker.load(ttsText, 'ja'); } catch {}
  }

  showScreen('translate');
}

async function onSearchSubmit() {
  const title = document.getElementById('search-title').value.trim();
  const artist = document.getElementById('search-artist').value.trim();
  const year = document.getElementById('search-year').value.trim();
  if (!title && !artist) { showToast('作品名または作者を入力してください'); return; }

  setLoadingMessage('作品を特定しています');
  showScreen('loading');

  try {
    const museumId = selectedMuseum?.id;
    const identified = await identifyByText({ title, artist, year, museumId });
    lastIdentified = identified;
    await processIdentified(identified, null);

    document.getElementById('search-title').value = '';
    document.getElementById('search-artist').value = '';
    document.getElementById('search-year').value = '';
  } catch (err) {
    showToast(err?.message || '識別に失敗しました');
    showScreen('search');
  }
}

async function processIdentified(identified, imageBlob) {
  if (identified.status === 'candidates' && Array.isArray(identified.candidates)) {
    currentVisit = {
      id: generateId(), createdAt: Date.now(), imageBlob,
      museumId: selectedMuseum?.id || null,
    };
    renderCandidates(identified.candidates);
    showScreen('candidates');
    return;
  }

  currentVisit = {
    id: generateId(),
    createdAt: Date.now(),
    imageBlob,
    title: identified.title || '',
    titleOriginal: identified.title_original || '',
    artist: identified.artist || '',
    year: identified.year || '',
    medium: identified.medium || '',
    genre: identified.genre || 'other',
    confidence: identified.confidence || '',
    summaryHint: identified.summary_hint || '',
    visualDescription: identified.visualDescription || '',
    museumId: selectedMuseum?.id || null,
    museumName: selectedMuseum?.name || '',
    language: selectedMuseum?.language || 'en',
    narrateMode: preferredNarrateMode,
  };

  if (identified.status === 'identified') {
    await runNarrationAndSpeak({
      mode: 'identified',
      title: identified.title, artist: identified.artist, year: identified.year,
      medium: identified.medium, museum: selectedMuseum ? selectedMuseum.name : '',
      summary_hint: identified.summary_hint,
    });
  } else {
    await runNarrationAndSpeak({
      mode: 'visual',
      visualDescription: identified.visualDescription || '',
    });
  }
}

async function runNarrationAndSpeak(payload, override = {}) {
  payload.narrateMode = payload.narrateMode || preferredNarrateMode;

  setLoadingMessage('解説を作成しています');
  showScreen('loading');

  let narration;
  try {
    const result = await narrate(payload);
    narration = result.narration || '';
  } catch (err) {
    showToast(err?.message || '解説の生成に失敗しました');
    showScreen('home');
    return;
  }

  setLoadingMessage('音声を生成しています');
  let audioBlob;
  try { audioBlob = await speaker.load(narration, 'ja'); }
  catch (err) { showToast(err?.message || '音声化に失敗しました'); }

  if (currentVisit) {
    currentVisit.narration = narration;
    currentVisit.narrateMode = payload.narrateMode;
    if (audioBlob) currentVisit.audioBlob = audioBlob;
    if (override.genre) currentVisit.genre = override.genre;
    if (payload.mode === 'identified') {
      currentVisit.title = payload.title || currentVisit.title;
      currentVisit.artist = payload.artist || currentVisit.artist;
      currentVisit.year = payload.year || currentVisit.year;
      currentVisit.medium = payload.medium || currentVisit.medium;
    } else if (payload.mode === 'visual') {
      currentVisit.title = currentVisit.title || '視覚的解説';
      currentVisit.visualDescription = payload.visualDescription || currentVisit.visualDescription;
    }
  }

  if (currentVisit?.title) lastVisitTitle = currentVisit.title;

  renderResult(currentVisit, narration);
  showScreen('result');

  // バックグラウンドで用語抽出 → glossary に蓄積
  if (narration && currentVisit) {
    extractAndStoreTerms(narration, currentVisit.id, currentVisit.title);
  }
}

async function extractAndStoreTerms(narration, visitId, visitTitle) {
  try {
    const terms = await extractTermsApi(narration);
    for (const t of terms) {
      await saveTerm({
        term: t.term,
        reading: t.reading,
        meaning: t.meaning,
        visitId,
        visitTitle,
      });
    }
  } catch {
    // サイレントに失敗（バックグラウンド処理なので、ユーザーには表示しない）
  }
}

async function togglePlay() {
  if (speaker.isPlaying()) {
    speaker.pause();
    setPlayIcon(false);
  } else {
    titleSpeaker.pause();
    try { await speaker.play(); setPlayIcon(true); }
    catch (err) { showToast(err?.message || '再生できません'); }
  }
}

async function playOriginalTitle() {
  if (!currentVisit?.titleOriginal) return;
  speaker.pause();
  setPlayIcon(false);
  try {
    await titleSpeaker.load(currentVisit.titleOriginal, currentVisit.language || 'en');
    await titleSpeaker.play();
  } catch (err) { showToast(err?.message || '原題の再生に失敗しました'); }
}

function setPlayIcon(playing) {
  const useEl = document.querySelector('#play-icon use');
  if (useEl) useEl.setAttribute('href', playing ? '#i-pause' : '#i-play');
}

// ===== Speed =====
function loadSpeed() {
  const saved = parseFloat(localStorage.getItem(SPEED_KEY));
  if (Number.isFinite(saved)) {
    const idx = SPEED_OPTIONS.indexOf(saved);
    if (idx >= 0) speedIdx = idx;
  }
  speaker.setPlaybackRate(SPEED_OPTIONS[speedIdx]);
  titleSpeaker.setPlaybackRate(1.0);
  updateSpeedLabel();
}

function cyclePlaybackRate() {
  speedIdx = (speedIdx + 1) % SPEED_OPTIONS.length;
  const rate = SPEED_OPTIONS[speedIdx];
  speaker.setPlaybackRate(rate);
  localStorage.setItem(SPEED_KEY, String(rate));
  updateSpeedLabel();
}

function updateSpeedLabel() {
  const btn = document.getElementById('btn-speed');
  if (btn) btn.textContent = `${SPEED_OPTIONS[speedIdx]}x`;
}

// ===== Narrate mode =====
function loadNarrateMode() {
  const saved = localStorage.getItem(NARRATE_MODE_KEY);
  if (NARRATE_MODES.some((m) => m.value === saved)) preferredNarrateMode = saved;
}

function setPreferredMode(mode) {
  preferredNarrateMode = mode;
  localStorage.setItem(NARRATE_MODE_KEY, mode);
  updateModeLabel();
}

function updateModeLabel() {
  const m = NARRATE_MODES.find((x) => x.value === preferredNarrateMode);
  const el = document.getElementById('btn-result-mode-label');
  if (el && m) el.textContent = `モード: ${m.label}`;
}

function renderModeList() {
  const list = document.getElementById('mode-list');
  list.innerHTML = '';
  for (const mode of NARRATE_MODES) {
    const li = document.createElement('li');
    li.className = 'mode-list__item' + (mode.value === preferredNarrateMode ? ' mode-list__item--active' : '');
    li.innerHTML = `
      <div class="mode-list__body">
        <h3 class="mode-list__title">${escapeHtml(mode.label)}</h3>
        <p class="mode-list__desc">${escapeHtml(mode.desc)}</p>
      </div>
      ${mode.value === preferredNarrateMode ? '<span class="mode-list__chip">current</span>' : ''}
    `;
    li.addEventListener('click', async () => {
      setPreferredMode(mode.value);
      await regenerateWithMode(mode.value);
    });
    list.appendChild(li);
  }
}

async function regenerateWithMode(mode) {
  if (!currentVisit) { showScreen('home'); return; }
  const wasIdentified = !!currentVisit.title && currentVisit.title !== '視覚的解説';
  const payload = wasIdentified
    ? {
        mode: 'identified',
        title: currentVisit.title,
        artist: currentVisit.artist,
        year: currentVisit.year,
        medium: currentVisit.medium,
        museum: currentVisit.museumName || (selectedMuseum?.name || ''),
        summary_hint: currentVisit.summaryHint || '',
        narrateMode: mode,
      }
    : {
        mode: 'visual',
        visualDescription: currentVisit.visualDescription || '',
        narrateMode: mode,
      };
  await runNarrationAndSpeak(payload);
}

function renderResult(visit, narration) {
  const imgEl = document.getElementById('result-image');
  const titleEl = document.getElementById('result-title');
  const originalEl = document.getElementById('result-original');
  const metaEl = document.getElementById('result-meta');
  const genreEl = document.getElementById('result-genre');
  const confEl = document.getElementById('result-confidence');
  const narrEl = document.getElementById('result-narration');
  const routeEl = document.getElementById('result-route');

  if (visit.imageBlob) {
    imgEl.src = URL.createObjectURL(visit.imageBlob);
    imgEl.hidden = false;
  } else {
    imgEl.removeAttribute('src');
    imgEl.hidden = true;
  }

  titleEl.textContent = visit.title || '解説';

  if (visit.titleOriginal && visit.titleOriginal !== visit.title) {
    originalEl.textContent = `${visit.titleOriginal} — タップで原語再生`;
    originalEl.hidden = false;
  } else {
    originalEl.textContent = '';
    originalEl.hidden = true;
  }

  const metaParts = [visit.artist, visit.year].filter(Boolean);
  metaEl.textContent = metaParts.join(' / ');
  metaEl.hidden = metaParts.length === 0;

  if (visit.genre) {
    genreEl.textContent = genreLabel(visit.genre);
    genreEl.hidden = false;
  } else { genreEl.hidden = true; }

  if (visit.confidence) {
    const labelMap = { high: 'high confidence', medium: 'medium confidence', low: 'low confidence' };
    confEl.textContent = labelMap[visit.confidence] || visit.confidence;
    confEl.hidden = false;
  } else { confEl.hidden = true; }

  narrEl.textContent = narration || '';

  if (selectedMuseum && visit.museumId && visit.museumId === selectedMuseum.id) {
    routeEl.hidden = false;
  } else { routeEl.hidden = true; }

  document.getElementById('progress-bar').style.width = '0';
  document.getElementById('result-time').textContent = '0:00';
  setPlayIcon(false);
  updateModeLabel();
  updateSpeedLabel();
}

function renderCandidates(candidates) {
  const list = document.getElementById('candidates-list');
  list.innerHTML = '';
  for (const c of candidates) {
    const li = document.createElement('li');
    li.className = 'candidate';
    const metaText = [c.artist, c.year].filter(Boolean).join(' / ');
    li.innerHTML = `
      <h3 class="candidate__title">${escapeHtml(c.title || '')}</h3>
      ${metaText ? `<p class="candidate__meta">${escapeHtml(metaText)}</p>` : ''}
      ${c.reason ? `<p class="candidate__reason">${escapeHtml(c.reason)}</p>` : ''}
    `;
    li.addEventListener('click', async () => {
      await runNarrationAndSpeak({
        mode: 'identified',
        title: c.title, artist: c.artist, year: c.year, medium: c.medium,
        museum: selectedMuseum ? selectedMuseum.name : '',
      }, { genre: c.genre || 'other' });
    });
    list.appendChild(li);
  }
}

// ===== Chat =====
function openChat() {
  if (!currentVisit) return;
  chatHistory = [];
  document.getElementById('chat-heading').textContent = 'ask';
  renderChat();
  showScreen('chat');
  setTimeout(() => document.getElementById('chat-input').focus(), 100);
}

function renderChat() {
  const list = document.getElementById('chat-list');
  const hint = document.getElementById('chat-hint');
  list.innerHTML = '';
  hint.hidden = chatHistory.length > 0;
  for (const m of chatHistory) {
    const li = document.createElement('li');
    li.className = `chat__item chat__item--${m.role}` + (m.loading ? ' chat__item--loading' : '');
    li.innerHTML = `
      <span class="chat__role">${m.role === 'user' ? 'You' : 'Guide'}</span>
      <div class="chat__bubble">${escapeHtml(m.content).replace(/\n/g, '<br>')}</div>
    `;
    list.appendChild(li);
  }
  list.scrollTop = list.scrollHeight;
}

async function onChatSubmit() {
  const input = document.getElementById('chat-input');
  const text = input.value.trim();
  if (!text || !currentVisit) return;

  chatHistory.push({ role: 'user', content: text });
  chatHistory.push({ role: 'assistant', content: '考えています…', loading: true });
  renderChat();
  input.value = '';

  try {
    const messagesForApi = chatHistory.filter((m) => !m.loading).map((m) => ({
      role: m.role, content: m.content,
    }));
    const answer = await askApi({
      context: {
        title: currentVisit.title || '',
        artist: currentVisit.artist || '',
        year: currentVisit.year || '',
        medium: currentVisit.medium || '',
        museum: currentVisit.museumName || (selectedMuseum?.name || ''),
        narration: currentVisit.narration || '',
      },
      messages: messagesForApi,
    });
    chatHistory.pop();
    chatHistory.push({ role: 'assistant', content: answer });
  } catch (err) {
    chatHistory.pop();
    chatHistory.push({ role: 'assistant', content: `エラー: ${err.message || '応答に失敗しました'}` });
  }
  renderChat();
}

// ===== History =====
async function renderHistory() {
  try { historyAll = await listVisits(200); }
  catch (e) { showToast('履歴の読み込みに失敗しました'); historyAll = []; }
  renderHistoryGenres();
  renderHistoryList();
}

function renderHistoryGenres() {
  const el = document.getElementById('history-genres');
  if (!el) return;
  el.innerHTML = '';

  const counts = { all: historyAll.length };
  for (const v of historyAll) {
    const g = v.genre || 'other';
    counts[g] = (counts[g] || 0) + 1;
  }

  for (const f of HISTORY_GENRE_FILTERS) {
    if (f.value !== 'all' && !counts[f.value]) continue;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chip' + (historyFilter.genre === f.value ? ' chip--active' : '');
    btn.innerHTML = `${escapeHtml(f.label)}<span class="chip__count">${counts[f.value] || 0}</span>`;
    btn.addEventListener('click', () => {
      historyFilter.genre = f.value;
      renderHistoryGenres();
      renderHistoryList();
    });
    el.appendChild(btn);
  }
}

function renderHistoryList() {
  const list = document.getElementById('history-list');
  const empty = document.getElementById('history-empty');
  list.innerHTML = '';

  let items = historyAll;
  if (historyFilter.genre !== 'all') {
    items = items.filter((v) => (v.genre || 'other') === historyFilter.genre);
  }
  if (historyFilter.search) {
    const q = historyFilter.search.toLowerCase();
    items = items.filter((v) =>
      (v.title || '').toLowerCase().includes(q) ||
      (v.artist || '').toLowerCase().includes(q) ||
      (v.year || '').toLowerCase().includes(q) ||
      (v.narration || '').toLowerCase().includes(q));
  }

  if (items.length === 0) { empty.hidden = false; return; }
  empty.hidden = true;

  for (const v of items) {
    list.appendChild(buildHistoryItem(v, async () => { await renderHistory(); }));
  }
}

function buildHistoryItem(v, onDeleted) {
  const li = document.createElement('li');
  li.className = 'history__item';
  li.dataset.visitId = v.id;
  const thumbUrl = v.imageBlob ? URL.createObjectURL(v.imageBlob) : '';
  const dateStr = new Date(v.createdAt).toLocaleString('ja-JP', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const metaText = [v.artist, v.year, dateStr].filter(Boolean).join(' / ');
  const genreText = v.genre ? genreLabel(v.genre) : '';

  const main = document.createElement('div');
  main.className = 'history__main';
  main.innerHTML = `
    ${thumbUrl ? `<img class="history__thumb" src="${escapeHtml(thumbUrl)}" alt="" />` : '<div class="history__thumb"></div>'}
    <div class="history__body">
      ${genreText ? `<span class="history__genre">${escapeHtml(genreText)}</span>` : ''}
      <h3 class="history__title">${escapeHtml(v.title || '視覚的解説')}</h3>
      <p class="history__meta">${escapeHtml(metaText)}</p>
    </div>
  `;
  main.addEventListener('click', async () => {
    const list = document.getElementById('history-list');
    const isPicker = list.classList.contains('compare-picker');

    const visit = await getVisit(v.id);
    if (!visit) return;

    if (isPicker) {
      // 比較ピッカーモード
      if (compareTargets.some((c) => c.visitId === visit.id)) {
        showToast('既に追加されています');
        return;
      }
      compareTargets.push({
        visitId: visit.id,
        title: visit.title,
        artist: visit.artist,
        year: visit.year,
        medium: visit.medium,
        summary: (visit.narration || '').slice(0, 300),
      });
      list.classList.remove('compare-picker');
      renderCompareTargets();
      showScreen('compare');
      return;
    }

    // 通常モード
    currentVisit = visit;
    if (visit.title) lastVisitTitle = visit.title;
    renderResult(visit, visit.narration || '');
    if (visit.audioBlob) {
      try { await speaker.loadFromBlob(visit.audioBlob); } catch {}
    }
    showScreen('result');
  });

  const delBtn = document.createElement('button');
  delBtn.className = 'history__delete';
  delBtn.type = 'button';
  delBtn.setAttribute('aria-label', '削除');
  delBtn.innerHTML = `<svg class="icon"><use href="#i-trash"/></svg>`;
  delBtn.addEventListener('click', async (event) => {
    event.stopPropagation();
    try {
      await deleteVisit(v.id);
      showToast('削除しました');
      if (typeof onDeleted === 'function') await onDeleted();
    } catch (e) { showToast('削除に失敗しました'); }
  });

  li.appendChild(main);
  li.appendChild(delBtn);
  return li;
}

// ===== Glossary =====
async function openGlossary() {
  glossaryFilter = '';
  document.getElementById('glossary-search').value = '';
  try { glossaryAll = await listTerms(200); }
  catch { glossaryAll = []; }
  renderGlossaryList();
  showScreen('glossary');
}

function renderGlossaryList() {
  const list = document.getElementById('glossary-list');
  const empty = document.getElementById('glossary-empty');
  list.innerHTML = '';

  let items = glossaryAll;
  if (glossaryFilter) {
    const q = glossaryFilter.toLowerCase();
    items = items.filter((t) =>
      (t.term || '').toLowerCase().includes(q) ||
      (t.reading || '').toLowerCase().includes(q) ||
      (t.meaning || '').toLowerCase().includes(q));
  }

  if (items.length === 0) { empty.hidden = false; return; }
  empty.hidden = true;

  for (const t of items) {
    const li = document.createElement('li');
    li.className = 'glossary__item';
    const occ = (t.occurrences || []).length;
    const lastTitle = (t.occurrences || []).slice(-1)[0]?.visitTitle || '';
    li.innerHTML = `
      <div class="glossary__head">
        <h3 class="glossary__term">${escapeHtml(t.term)}</h3>
        ${t.reading ? `<span class="glossary__reading">${escapeHtml(t.reading)}</span>` : ''}
        ${occ > 1 ? `<span class="glossary__count">×${occ}</span>` : ''}
      </div>
      <p class="glossary__meaning">${escapeHtml(t.meaning)}</p>
      ${lastTitle ? `<p class="glossary__occurrences">${escapeHtml(lastTitle)} で出会った用語</p>` : ''}
    `;
    list.appendChild(li);
  }
}

// ===== Compare =====
function openCompareFromResult() {
  if (!currentVisit) { showScreen('home'); return; }
  // 現在の作品を1つ目として追加
  compareTargets = [{
    visitId: currentVisit.id,
    title: currentVisit.title,
    artist: currentVisit.artist,
    year: currentVisit.year,
    medium: currentVisit.medium,
    summary: (currentVisit.narration || '').slice(0, 300),
  }];
  renderCompareTargets();
  showScreen('compare');
}

function renderCompareTargets() {
  const wrap = document.getElementById('compare-targets');
  wrap.innerHTML = '';
  for (let i = 0; i < compareTargets.length; i++) {
    const w = compareTargets[i];
    const div = document.createElement('div');
    div.className = 'compare__target';
    div.innerHTML = `
      <span class="compare__target-num">${i + 1}</span>
      <p class="compare__target-title">${escapeHtml(w.title || '(無題)')}</p>
      <button class="compare__target-remove" type="button" data-idx="${i}">remove</button>
    `;
    div.querySelector('.compare__target-remove').addEventListener('click', () => {
      compareTargets.splice(i, 1);
      renderCompareTargets();
    });
    wrap.appendChild(div);
  }
  if (compareTargets.length < 4) {
    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'compare__add';
    addBtn.textContent = compareTargets.length === 0 ? '+ 履歴から作品を選ぶ' : '+ もう1作品追加';
    addBtn.addEventListener('click', async () => {
      await openComparePicker();
    });
    wrap.appendChild(addBtn);
  }

  const runBtn = document.getElementById('btn-compare-run');
  runBtn.disabled = compareTargets.length < 2;
  document.getElementById('compare-result').hidden = true;
}

async function openComparePicker() {
  await renderHistory();
  const list = document.getElementById('history-list');
  if (list.children.length === 0) {
    showToast('履歴がありません。先に作品を鑑賞してください');
    return;
  }
  list.classList.add('compare-picker');
  showToast('比較に追加する作品を選んでください');
  showScreen('history');
}

async function runCompare() {
  if (compareTargets.length < 2) {
    showToast('2作品以上を選択してください');
    return;
  }
  const btn = document.getElementById('btn-compare-run');
  btn.disabled = true;
  const resultEl = document.getElementById('compare-result');
  resultEl.hidden = false;
  resultEl.textContent = '比較解説を生成しています…';

  try {
    const comparison = await compareWorksApi(compareTargets);
    resultEl.textContent = comparison;
    // 音声化
    try { await speaker.load(comparison, 'ja'); }
    catch {}
  } catch (err) {
    resultEl.textContent = '';
    resultEl.hidden = true;
    showToast(err?.message || '比較解説の生成に失敗しました');
  } finally {
    btn.disabled = false;
  }
}

// ===== Exhibitions =====
function bindExhibitionsScreen() {
  document.getElementById('btn-exhibitions-back').addEventListener('click', () => {
    showScreen('home');
  });
  document.getElementById('btn-exhibitions-refresh').addEventListener('click', async () => {
    if (selectedMuseum?.id) await loadExhibitions(selectedMuseum.id, true);
  });
}

function bindPreparationScreen() {
  document.getElementById('btn-preparation-back').addEventListener('click', () => {
    speaker.pause(); setPlayIcon(false);
    showScreen('exhibitions');
  });
}

async function openExhibitions() {
  if (!selectedMuseum) {
    showToast('まず美術館を選んでください');
    return;
  }
  document.getElementById('exhibitions-title').textContent = (selectedMuseum.nameJa || selectedMuseum.name).slice(0, 30);
  document.getElementById('exhibitions-list').innerHTML = '';
  setExhibitionsStatus('読み込み中…');
  showScreen('exhibitions');
  await loadExhibitions(selectedMuseum.id, false);
}

async function loadExhibitions(museumId, forceRefresh) {
  setExhibitionsStatus('展覧会情報を取得しています（最大30秒）…');
  try {
    // Web Search を伴う処理は時間かかるので、明示的に待つ
    const result = await fetchExhibitionsApi(museumId);
    currentExhibitions = result;
    renderExhibitionsList(result);
  } catch (err) {
    setExhibitionsStatus(err?.message || '取得に失敗しました');
    showToast(err?.message || '取得に失敗しました');
  }
}

function setExhibitionsStatus(text) {
  const el = document.getElementById('exhibitions-status');
  if (!el) return;
  if (!text) { el.hidden = true; el.textContent = ''; }
  else { el.hidden = false; el.textContent = text; }
}

function renderExhibitionsList(result) {
  const list = document.getElementById('exhibitions-list');
  list.innerHTML = '';

  const items = result?.exhibitions || [];
  if (items.length === 0) {
    setExhibitionsStatus(result?.fallback
      ? 'Web 検索が利用できませんでした。常設展示・代表的な展覧会の傾向を表示します。'
      : '現在開催中の展覧会情報が見つかりませんでした。');
    return;
  }

  const fetchedDate = new Date(result.fetchedAt);
  const dateStr = fetchedDate.toLocaleString('ja-JP', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
  const cachedTag = result.cached ? '（キャッシュ）' : (result.fallback ? '（Web検索なし）' : '');
  setExhibitionsStatus(`最終更新: ${dateStr}${cachedTag}`);

  for (const ex of items) {
    const li = document.createElement('li');
    li.className = 'exhibition';
    const categoryLabel = ex.category && ex.category !== 'other' ? translateCategory(ex.category) : '';
    li.innerHTML = `
      ${categoryLabel ? `<p class="exhibition__category">${escapeHtml(categoryLabel)}</p>` : ''}
      <h3 class="exhibition__title">${escapeHtml(ex.title_ja || ex.title || '')}</h3>
      ${ex.title && ex.title_ja && ex.title !== ex.title_ja ? `<p class="exhibition__title-en">${escapeHtml(ex.title)}</p>` : ''}
      ${ex.period ? `<p class="exhibition__period">${escapeHtml(ex.period)}</p>` : ''}
      ${ex.summary ? `<p class="exhibition__summary">${escapeHtml(ex.summary)}</p>` : ''}
      ${ex.artists?.length ? `<div class="exhibition__chips">${ex.artists.slice(0, 5).map((a) => `<span class="exhibition__chip">${escapeHtml(a)}</span>`).join('')}</div>` : ''}
      <div style="margin-top:10px">
        <a class="exhibition__link" data-action="prepare" href="javascript:void(0)">予習する</a>
        ${ex.url ? `<a class="exhibition__link" href="${escapeHtml(ex.url)}" target="_blank" rel="noopener noreferrer">公式</a>` : ''}
      </div>
    `;
    li.querySelector('[data-action="prepare"]').addEventListener('click', async (event) => {
      event.stopPropagation();
      currentExhibition = ex;
      await openPreparation(ex);
    });
    list.appendChild(li);
  }
}

function translateCategory(cat) {
  const map = {
    special_exhibition: '企画展',
    retrospective: '回顧展',
    thematic: 'テーマ展',
    photography: '写真展',
    contemporary: '現代美術',
    historical: '歴史展',
  };
  return map[cat] || '';
}

async function openPreparation(exhibition) {
  setLoadingMessage('予習資料を作成しています');
  showScreen('loading');

  try {
    const result = await generatePreparationApi(selectedMuseum.id, exhibition);
    currentPreparation = result;
    renderPreparation(exhibition, result);
    showScreen('preparation');
    // 自動で予習解説を音声化
    if (result.preparation) {
      try { await speaker.load(result.preparation, 'ja'); } catch {}
    }
  } catch (err) {
    showToast(err?.message || '予習資料の生成に失敗しました');
    showScreen('exhibitions');
  }
}

function renderPreparation(exhibition, prep) {
  document.getElementById('preparation-title').textContent = exhibition.title_ja || exhibition.title;
  document.getElementById('preparation-period').textContent = exhibition.period || '';
  document.getElementById('preparation-text').textContent = prep.preparation || '';

  // sections
  const sectionsWrap = document.getElementById('preparation-sections-wrap');
  const sectionsList = document.getElementById('preparation-sections');
  sectionsList.innerHTML = '';
  if (prep.sections && prep.sections.length > 0) {
    for (const s of prep.sections) {
      const li = document.createElement('li');
      li.className = 'preparation__section';
      li.innerHTML = `
        <h3 class="preparation__section-title">${escapeHtml(s.title)}</h3>
        <p class="preparation__section-desc">${escapeHtml(s.description)}</p>
      `;
      sectionsList.appendChild(li);
    }
    sectionsWrap.hidden = false;
  } else {
    sectionsWrap.hidden = true;
  }

  // featured works
  const worksWrap = document.getElementById('preparation-works-wrap');
  const worksList = document.getElementById('preparation-works');
  worksList.innerHTML = '';
  if (prep.featured_works && prep.featured_works.length > 0) {
    for (const w of prep.featured_works) {
      const li = document.createElement('li');
      li.className = 'preparation__work';
      const meta = [w.artist, w.year, w.medium].filter(Boolean).join(' / ');
      li.innerHTML = `
        <h3 class="preparation__work-title">${escapeHtml(w.title_ja || w.title || '(無題)')}</h3>
        ${meta ? `<p class="preparation__work-meta">${escapeHtml(meta)}</p>` : ''}
        ${w.highlight ? `<p class="preparation__work-highlight">${escapeHtml(w.highlight)}</p>` : ''}
        <span class="preparation__work-cta">この作品を詳しく聴く →</span>
      `;
      li.addEventListener('click', async () => {
        speaker.pause(); setPlayIcon(false);
        await runWorkPreviewFromExhibition(w);
      });
      worksList.appendChild(li);
    }
    worksWrap.hidden = false;
  } else {
    worksWrap.hidden = true;
  }

  // route
  const routeWrap = document.getElementById('preparation-route-wrap');
  if (prep.viewing_route) {
    document.getElementById('preparation-route-text').textContent = prep.viewing_route;
    routeWrap.hidden = false;
  } else {
    routeWrap.hidden = true;
  }

  setPlayIcon(false);
  document.getElementById('progress-bar').style.width = '0';
  document.getElementById('result-time').textContent = '0:00';
}

async function runWorkPreviewFromExhibition(work) {
  // 個別作品の解説生成 → result 画面で表示
  currentVisit = {
    id: generateId(),
    createdAt: Date.now(),
    imageBlob: null,
    title: work.title_ja || work.title,
    titleOriginal: work.title || '',
    artist: work.artist || '',
    year: work.year || '',
    medium: work.medium || '',
    genre: 'other',
    confidence: '',
    summaryHint: work.highlight || '',
    visualDescription: '',
    museumId: selectedMuseum?.id || null,
    museumName: selectedMuseum?.name || '',
    language: selectedMuseum?.language || 'en',
    narrateMode: preferredNarrateMode,
  };

  await runNarrationAndSpeak({
    mode: 'identified',
    title: work.title || work.title_ja,
    artist: work.artist,
    year: work.year,
    medium: work.medium,
    museum: selectedMuseum?.name || '',
    summary_hint: work.highlight || '',
  });
}

async function renderRecentOnHome() {
  const section = document.getElementById('home-recent');
  const list = document.getElementById('recent-list');
  if (!section || !list) return;

  let items = [];
  try { items = await listVisits(5); }
  catch (e) { section.hidden = true; return; }

  if (items.length === 0) { section.hidden = true; return; }

  list.innerHTML = '';
  for (const v of items) {
    const li = document.createElement('li');
    li.className = 'recent__item';
    const thumbUrl = v.imageBlob ? URL.createObjectURL(v.imageBlob) : '';
    const metaText = [v.artist, v.year].filter(Boolean).join(' / ');
    li.innerHTML = `
      ${thumbUrl ? `<img class="recent__thumb" src="${escapeHtml(thumbUrl)}" alt="" />` : '<div class="recent__thumb"></div>'}
      <div class="recent__body">
        <h3 class="recent__title">${escapeHtml(v.title || '視覚的解説')}</h3>
        <p class="recent__meta">${escapeHtml(metaText)}</p>
      </div>
    `;
    li.addEventListener('click', async () => {
      const visit = await getVisit(v.id);
      if (!visit) return;
      currentVisit = visit;
      if (visit.title) lastVisitTitle = visit.title;
      renderResult(visit, visit.narration || '');
      if (visit.audioBlob) {
        try { await speaker.loadFromBlob(visit.audioBlob); } catch {}
      }
      showScreen('result');
    });
    list.appendChild(li);
  }
  section.hidden = false;
}
